import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Typography,
  TextField,
  Avatar,
  Stack,
  CircularProgress,
  Alert,
  AlertTitle,
  Grid,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { useNavigate, useParams } from "react-router-dom";
import { API_ENDPOINT } from "../../utils/constants";
import { getErrorResponse, toastError } from "../../utils/util";
import { LoadingButton } from "@mui/lab";
import { toast } from "react-toastify";
import { MuiTelInput } from "mui-tel-input";
import CountrySelector from "../widgets/CountrySelector";
import StateSelector from "../widgets/StateSelector";
import CitySelector from "../widgets/CitySelector";

const VisitorCreateProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [profileImage, setProfileImage] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [aadharImage, setAadharImage] = useState(null);
  const [aadharFile, setAadharFile] = useState(null);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [country, setCountry] = useState(null);
  const [state, setState] = useState(null);
  const [city, setCity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [requestSuccess, setRequestSuccess] = useState(false);
  const [upLoading, setUpLoading] = useState(false);
  const [verificationMessage, setVerificationMessage] = useState(null);
  const [verificationError, setVerificationError] = useState(null);

  useEffect(() => {
    const verifyToken = async () => {
      try {
        setLoading(true);
        const { data } = await axios.get(
          `${API_ENDPOINT}user/verify-visitor-reg-token?id=${id}`
        );
        setVerificationMessage(data.message);
        setCompanyInfo(data.data);
      } catch (error) {
        setVerificationError(getErrorResponse(error));
      } finally {
        setLoading(false);
      }
    };

    verifyToken();
  }, [id]);

  const handleImageUpload = (event, type) => {
    const file = event.target.files[0];
    if (file) {
      if (type === "profile") {
        setSelectedFile(file);
        setProfileImage(URL.createObjectURL(file));
      } else if (type === "aadhar") {
        setAadharFile(file);
        setAadharImage(URL.createObjectURL(file));
      }
    }
  };

  const handleSubmit = async () => {
    if (!name.trim() || !profileImage || !aadharImage) {
      toast.error("All fields are required, including Aadhaar document.");
      return;
    }
    if (!profileImage) {
      toast.error("Profile image is required!");
      return;
    }
    if (!name.trim()) {
      toast.error("Name is required!");
      return;
    }
    if (country === null) {
      toast.error("Country is required!");
      return;
    }
    if (state === null) {
      toast.error("State is required!");
      return;
    }
    if (city === null) {
      toast.error("City is required!");
      return;
    }
    if (!aadharImage) {
      toast.error("Aadhaar document is required!");
      return;
    }
    try {
      setUpLoading(true);
      const formData = new FormData();
      formData.append("image", selectedFile);
      formData.append("aadhar", aadharFile);
      formData.append("name", name);
      formData.append("phone", phone);
      formData.append("country", JSON.stringify(country));
      formData.append("state", JSON.stringify(state));
      formData.append("city", JSON.stringify(city));
      const { data } = await axios.post(
        `${API_ENDPOINT}user/register-visitor?id=${id}`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      toast.success(data.message);
      setTimeout(() => {
        navigate(`/visitor-profile/${data.visitorToken}`);
      }, 100);
    } catch (error) {
      toastError(error);
    } finally {
      setUpLoading(false);
    }
  };

  return requestSuccess ? (
    <Container maxWidth="sm">
      <Alert severity="success">
        <AlertTitle>Profile Created Successfully</AlertTitle>
        Your profile has been created successfully! Check your email for further
        instructions.
      </Alert>
    </Container>
  ) : (
    <Container maxWidth="sm" sx={{ mt: 4, mb: 4 }}>
      {/* Banner and Logo Section */}
      <Box
        sx={{
          position: "relative",
          width: "100%",
          height: 200, // Fixed height for 3:1 banner
          mb: -8, // Overlap with the card
        }}
      >
        {companyInfo?.settings?.bnLogo && (
          <Box
            component="img"
            src={companyInfo.settings.bnLogo}
            alt={`${companyInfo?.company} banner`}
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              borderRadius: "8px 8px 0 0",
              boxShadow: 2,
            }}
          />
        )}
        {companyInfo?.settings?.srLogo && (
          <Avatar
            src={companyInfo.settings.srLogo}
            alt={`${companyInfo?.company} logo`}
            sx={{
              width: 120,
              height: 120,
              position: "absolute",
              bottom: -60, // Half of the avatar hangs below the banner
              left: "50%",
              transform: "translateX(-50%)",
              border: "4px solid white",
              bgcolor: "grey.100",
              boxShadow: 3,
            }}
          />
        )}
      </Box>

      <Card sx={{ mt: 8, p: 3, textAlign: "center", boxShadow: 3 }}>
        <CardContent>
          {loading && (
            <Box display="flex" justifyContent="center" my={2}>
              <CircularProgress />
            </Box>
          )}
          {verificationError && (
            <Alert severity="error">{verificationError}</Alert>
          )}
          {!loading && !verificationError && (
            <>
              <Typography
                variant="h4"
                fontWeight="bold"
                gutterBottom
                sx={{ mt: 4 }}
              >
                Welcome to {companyInfo?.company}!
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 3 }}>
                Join our community and set up your profile to get started.
              </Typography>
              <Stack spacing={2} alignItems="center">
                <Avatar src={profileImage} sx={{ width: 100, height: 100 }}>
                  {!profileImage && "?"}
                </Avatar>
                <Button
                  size="small"
                  variant="outlined"
                  component="label"
                  startIcon={<CloudUploadIcon />}
                  sx={{ borderRadius: 20 }}
                >
                  Upload Profile Image *
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e, "profile")}
                  />
                </Button>
              </Stack>

              <TextField
                required
                size="small"
                fullWidth
                label="Name"
                variant="outlined"
                sx={{ mt: 3 }}
                value={name}
                onChange={(e) => setName(e.target.value)}
              />

              <MuiTelInput
                sx={{ mt: 3 }}
                fullWidth
                size="small"
                label="Mobile No"
                defaultCountry="IN"
                forceCallingCode
                value={phone}
                onChange={(newValue) => setPhone(newValue)}
              />

              <Box mt={3}>
                <CountrySelector
                  required={true}
                  selectedCountry={country}
                  onChange={(newV) => setCountry(newV)}
                />
              </Box>
              <Grid mt={2} container spacing={1}>
                <Grid item xs={12} sm={6}>
                  <StateSelector
                    required={true}
                    countryCode={country?.code}
                    selectedState={state}
                    onChange={(newV) => setState(newV)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <CitySelector
                    required={true}
                    selectedCity={city}
                    onChange={(newV) => setCity(newV)}
                    countryCode={country?.code}
                    stateCode={state?.code}
                  />
                </Grid>
              </Grid>

              {/* Aadhaar Upload Section */}
              <Stack spacing={2} alignItems="center" mt={3}>
                {aadharImage && (
                  <img
                    src={aadharImage}
                    alt="Aadhaar Preview"
                    style={{ width: 200, borderRadius: 8 }}
                  />
                )}
                <Button
                  size="small"
                  variant="outlined"
                  component="label"
                  startIcon={<CloudUploadIcon />}
                  sx={{ borderRadius: 20 }}
                >
                  Upload Aadhaar Document *
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e, "aadhar")}
                  />
                </Button>
                <Alert severity="info">
                  Uploading your Aadhaar document helps verify your identity and
                  ensures a secure, seamless experience when connecting with the
                  people you want to meet.
                </Alert>
              </Stack>

              <LoadingButton
                fullWidth
                loading={upLoading}
                variant="contained"
                sx={{ mt: 3, borderRadius: 20, py: 1.5 }}
                onClick={handleSubmit}
                disabled={
                  !name ||
                  !profileImage ||
                  !aadharImage ||
                  country === null ||
                  state === null ||
                  city === null
                }
              >
                Join BetaZen
              </LoadingButton>
            </>
          )}
        </CardContent>
      </Card>
    </Container>
  );
};

export default VisitorCreateProfile;
